import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import dayjs from 'dayjs';
import {
  DATE_ONLY_FORMAT,
  MiddlewareBaseWithExtensionResponse,
  ReservationLookupResult,
  ReservationSearch,
} from '@ireckonu/shared-ui';

import { APP_ENV } from '../app-config';
import { ReservationSearchMappingService } from './reservation-search-mapping.service';
import { mapSortDirection, trimOrNull } from '../../utils';
import {
  ReservationInsight,
  ReservationSearchRequest,
  ReservationSortBy,
  ReservationSortDirection,
  ReservationsSearchQuery,
  ReservationsSearchResponse,
  ServiceUrlKey,
  SortOptionsEnum,
  SortOptionsType,
} from '../models';
import { HotelReservation } from '../models/hotel-reservations.api';
import {
  ReservationsSearchQueryApiInterface,
  RoomReservationApiInterface,
} from '../models/room-reservations.api';
import { ConfigurationQueryService } from './configuration-query.service';

const RESERVATIONS_PAGE_SIZE = 20;

interface ReservationSearchOptions {
  date?: Date | null;
  insight?: ReservationInsight | null;
  page?: number;
  pageSize?: number;
  includeOverview?: boolean;
}

@Injectable({ providedIn: 'root' })
export class HotelReservationsService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigurationQueryService);
  private readonly mappingService = inject(ReservationSearchMappingService);

  private readonly configApiBaseUrl =
    inject(APP_ENV).configApiBaseUrl + '/api/v1';

  private get coreApiBaseUrl(): string {
    return this.configService.getServiceUrl(ServiceUrlKey.CoreApiBaseUrl) ?? '';
  }
  searchReservations(
    query: ReservationsSearchQuery | null,
    hotelCode?: string,
    options: ReservationSearchOptions = {},
  ): Observable<ReservationsSearchResponse> {
    const requestBody = this.buildSearchRequest(
      query,
      hotelCode ?? '',
      options,
    );

    return this.http
      .post<
        MiddlewareBaseWithExtensionResponse<ReservationsSearchResponse>
      >(`${this.configApiBaseUrl}/reservations/hotel/search`, requestBody)
      .pipe(map((response) => response.Data));
  }

  getReservationDetailsById(
    reservationId: string,
    hotelCode?: string,
  ): Observable<HotelReservation> {
    return this.http
      .get<MiddlewareBaseWithExtensionResponse<HotelReservation>>(
        `${this.configApiBaseUrl}/reservations/hotel/${reservationId}`,
        {
          params: {
            hotelCode: hotelCode ?? '',
          },
        },
      )
      .pipe(map((response) => response.Data));
  }

  // INFO: this is deprecated API but the new reservations API cannot be used in the profiles tab
  searchProfileReservations(
    query: ReservationsSearchQueryApiInterface | null,
    hotelCode?: string,
  ): Observable<RoomReservationApiInterface[]> {
    const requestBody: ReservationSearch = query
      ? this.mappingService.mapSearchQueryToRequest(query, hotelCode ?? '')
      : this.mappingService.mapSearchQueryToRequest({}, hotelCode ?? '');

    return this.http
      .post<
        MiddlewareBaseWithExtensionResponse<ReservationLookupResult[]>
      >(`${this.coreApiBaseUrl}/reservation/search`, requestBody)
      .pipe(
        map((response) =>
          this.mappingService.mapSearchResultsToReservations(response),
        ),
      );
  }

  private buildSearchRequest(
    query: ReservationsSearchQuery | null,
    hotelCode: string,
    options: ReservationSearchOptions,
  ): ReservationSearchRequest {
    return {
      HotelCode: hotelCode || null,
      Date: options.date ? dayjs(options.date).format(DATE_ONLY_FORMAT) : null,
      ReservationId: trimOrNull(query?.reservationId),
      ReservationNumber: trimOrNull(query?.reservationNumber),
      UnitCode: trimOrNull(query?.roomNumber),
      PersonId: query?.guestId ?? null,
      FirstName: trimOrNull(query?.firstName),
      LastName: trimOrNull(query?.lastName),
      Email: trimOrNull(query?.email),
      Phone: trimOrNull(query?.phone),
      MembershipId: trimOrNull(query?.membershipId),
      LoyaltyTierCode: trimOrNull(query?.loyaltyTierCode),
      Statuses: query?.reservationStatus ?? [],
      Insight: options.insight ?? null,
      SortBy: this.mapSortBy(query?.sort),
      SortDirection: mapSortDirection(query?.sortDirection, {
        ascending: ReservationSortDirection.Ascending,
        descending: ReservationSortDirection.Descending,
      }),
      Page: options.page ?? 0,
      PageSize: options.pageSize ?? RESERVATIONS_PAGE_SIZE,
      IncludeOverview: options.includeOverview ?? true,
    };
  }

  private mapSortBy(sort?: SortOptionsType): ReservationSortBy | null {
    switch (sort) {
      case SortOptionsEnum.rfmTier:
        return ReservationSortBy.RfmScore;
      case SortOptionsEnum.roomType:
        return ReservationSortBy.RoomType;
      case SortOptionsEnum.lastName:
        return ReservationSortBy.LastName;
      case SortOptionsEnum.membershipTier:
        return ReservationSortBy.LoyaltyTier;
      case SortOptionsEnum.vipTier:
        return ReservationSortBy.VipTier;
      default:
        return null;
    }
  }
}
